import html2pdf from "html2pdf.js";

const BRAND = "#004030";

// Statuses that count as "in progress / not yet complete"
const PENDING_STATES = ["", "Draft", "New", null, undefined];
export const isPending = (status) => PENDING_STATES.includes(status);
export const displayStatus = (status) => (isPending(status) ? "Pending" : status);

const esc = (v) => String(v ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

// Colour-coded section palette (international admissions look)
export const SECTION_PALETTE = {
  "Personal": { hex: "#004030", bg: "#f0f7f4", border: "#a3d9c9" },
  "Address": { hex: "#1e3a8a", bg: "#eff6ff", border: "#bfdbfe" },
  "Programme": { hex: "#581c87", bg: "#faf5ff", border: "#e9d5ff" },
  "English proficiency": { hex: "#065f46", bg: "#ecfdf5", border: "#a7f3d0" },
  "Passport & visa": { hex: "#831843", bg: "#fdf2f8", border: "#fbcfe8" },
  "Guardian / emergency": { hex: "#9a3412", bg: "#fff7ed", border: "#ffedd5" },
  "Emergency contact": { hex: "#9f1239", bg: "#fff1f2", border: "#fecdd3" },
  "Funding": { hex: "#854d0e", bg: "#fefce8", border: "#fde68a" },
  "Statement of purpose": { hex: "#312e81", bg: "#eef2ff", border: "#c7d2fe" },
  "Education history": { hex: "#134e4a", bg: "#f0fdfa", border: "#99f6e4" },
  "Documents": { hex: "#4c1d95", bg: "#f5f3ff", border: "#ddd6fe" },
};
export const sectionColor = (title) => SECTION_PALETTE[title] || { hex: "#004030", bg: "#f0f7f4", border: "#cfe3db" };

function docShell(title, bodyHtml) {
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
  <style>
    @media print { body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; } .sec, .status, .head, .band { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; } table, .secwrap { page-break-inside: avoid; } }
    *{box-sizing:border-box;}
    body{margin:0;color:#1a231f;background:#fcfbf7;font-family:Georgia,'Times New Roman',serif;}
    .page{max-width:780px;margin:0 auto;padding:36px 40px 48px;}
    .head{display:flex;justify-content:space-between;align-items:center;background:#004030;color:#fff;border-radius:12px;padding:20px 24px;margin-bottom:8px;border-bottom:4px solid #c9a227;}
    .crest{width:52px;height:52px;border-radius:50%;border:2px solid #c9a227;background:#002b20;color:#c9a227;display:flex;align-items:center;justify-content:center;font-size:22px;font-weight:bold;margin-right:14px;}
    .brand{font-size:20px;font-weight:bold;letter-spacing:.3px;}
    .brand small{display:block;font-size:9px;color:#e8d9a8;font-weight:normal;letter-spacing:3px;margin-top:3px;}
    .docmeta{text-align:right;font-size:10px;color:#cfe0d8;line-height:1.7;}
    .band{display:flex;justify-content:space-between;align-items:center;background:#fff;border:1px solid #e2e8e4;border-radius:10px;padding:14px 20px;margin:12px 0 22px;}
    h1{font-size:19px;color:#004030;margin:0;}
    .sub{color:#57635e;font-size:12px;margin:3px 0 0;}
    .status{display:inline-block;padding:6px 16px;border-radius:999px;font-size:11px;font-weight:bold;letter-spacing:1px;}
    .secwrap{background:#fff;border:1px solid #e2e8e4;border-radius:10px;overflow:hidden;margin:0 0 16px;}
    .sec{font-size:12px;font-weight:bold;letter-spacing:1px;padding:11px 16px;text-transform:uppercase;}
    table{width:100%;border-collapse:collapse;}
    td{padding:9px 16px;border-bottom:1px solid #f1f5f3;font-size:13px;vertical-align:top;}
    tr:last-child td{border-bottom:none;}
    td.k{color:#57635e;width:40%;font-size:10px;text-transform:uppercase;letter-spacing:.6px;font-weight:bold;}
    td.v{font-weight:600;color:#1a231f;}
    ul.docs{margin:8px 16px;padding-left:18px;}
    ul.docs li{font-size:12px;margin:4px 0;}
    .foot{margin-top:34px;border-top:2px solid #c9a227;padding-top:12px;font-size:10px;color:#889690;text-align:center;line-height:1.7;}
  </style></head><body><div class="page">${bodyHtml}</div></body></html>`;
}

// opts: { heading, student, status, rows:[[k,v]], sections:[{title, rows}], documents:[names], docLabel }
function buildHtml(opts) {
  const { heading, student, status, rows = [], sections = [], documents = [], docLabel = "Application" } = opts;
  const pending = isPending(status);
  const statusColor = pending ? "background:#fef3c7;color:#92400e;" : "background:#dcfce7;color:#166534;";
  const rowHtml = (list) => list.filter(([, v]) => v !== undefined && v !== null && v !== "").map(([k, v]) => `<tr><td class="k">${esc(k)}</td><td class="v">${esc(v)}</td></tr>`).join("");
  const secHtml = sections.map((s) => {
    const rh = rowHtml(s.rows || []);
    if (!rh) return "";
    const c = sectionColor(s.title);
    return `<div class="secwrap"><div class="sec" style="background:${c.bg};color:${c.hex};border-left:5px solid ${c.hex};">${esc(s.title)}</div><table>${rh}</table></div>`;
  }).join("");
  const docsColor = sectionColor("Documents");
  const docsHtml = documents.length
    ? `<div class="secwrap"><div class="sec" style="background:${docsColor.bg};color:${docsColor.hex};border-left:5px solid ${docsColor.hex};">Supporting Documents</div><ul class="docs">${documents.map((d) => `<li>${esc(d)}</li>`).join("")}</ul></div>`
    : "";
  const body = `
    <div class="head">
      <div style="display:flex;align-items:center;"><div class="crest">G</div><div class="brand">GDIH Elite Education<small>STUDY ABROAD &bull; CAREER GUIDANCE</small></div></div>
      <div class="docmeta">${esc(docLabel)}<br/>Ref ${esc((opts.ref || heading || "").toString().slice(0, 10).toUpperCase())}<br/>${new Date().toLocaleDateString()}</div>
    </div>
    <div class="band">
      <div><h1>${esc(heading)}</h1>${student ? `<p class="sub">Applicant: ${esc(student)}</p>` : ""}</div>
      <span class="status" style="${statusColor}">${pending ? "PENDING" : esc(String(status).toUpperCase())}</span>
    </div>
    ${rows.filter(([, v]) => v).length ? `<table style="background:#fff;border:1px solid #e2e8e4;border-radius:10px;margin-bottom:16px;">${rowHtml(rows)}</table>` : ""}
    ${secHtml}
    ${docsHtml}
    <div class="foot">This document was generated from the GDIH Elite Education admissions portal.<br/>GDIH ELITE EDUCATION &bull; Your trusted study-abroad partner.</div>
  `;
  return docShell(heading, body);
}

export function printAppDoc(opts) {
  const html = buildHtml(opts);
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); }, 350);
}

export function downloadAppDoc(opts) {
  const html = buildHtml(opts);
  const el = document.createElement("div");
  el.innerHTML = html;
  const safe = String(opts.heading || "application").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  html2pdf().set({
    margin: 0,
    filename: `${opts.docLabel || "GDIH"}-${safe}.pdf`,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true },
    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
  }).from(el).save();
}
